import type { DisplayCell, SeparatorChar } from "./types";

/**
 * Normalisation des réponses françaises (ENG-002, ENG-010, CNT-004).
 *
 * Règle figée (ADR-0001 §D8) : la comparaison ignore accents, cédilles, casse,
 * espaces, apostrophes et tirets. La grille affiche des capitales non accentuées ;
 * l'orthographe exacte n'apparaît que sur l'écran de résultat.
 */

/** Variantes typographiques rencontrées dans un corpus français. */
const SEPARATORS: ReadonlyMap<string, SeparatorChar> = new Map<string, SeparatorChar>([
  ["-", "-"],
  ["‐", "-"], // tiret
  ["‑", "-"], // tiret insécable
  ["‒", "-"],
  ["–", "-"], // demi-cadratin
  ["—", "-"], // cadratin
  ["'", "'"],
  ["’", "'"], // apostrophe typographique
  ["‘", "'"],
  [" ", " "],
  [" ", " "], // espace insécable
  [" ", " "], // espace fine insécable
]);

const LIGATURES: ReadonlyMap<string, string> = new Map([
  ["œ", "oe"], // œ
  ["Œ", "OE"], // Œ
  ["æ", "ae"], // æ
  ["Æ", "AE"], // Æ
]);

export function isSeparator(char: string): boolean {
  return SEPARATORS.has(char);
}

export function separatorOf(char: string): SeparatorChar | null {
  return SEPARATORS.get(char) ?? null;
}

/**
 * Développe les ligatures avant tout autre traitement : « cœur » compte cinq
 * positions dans la grille, pas quatre.
 */
export function expandLigatures(raw: string): string {
  let out = "";
  for (const char of raw) {
    out += LIGATURES.get(char) ?? char;
  }
  return out;
}

/** Réduit un caractère à une lettre A–Z, ou `null` s'il n'en est pas une. */
function toPlainLetter(char: string): string | null {
  const stripped = char
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toUpperCase();
  return /^[A-Z]$/.test(stripped) ? stripped : null;
}

/**
 * Forme canonique utilisée par toutes les comparaisons du moteur.
 * Les caractères non reconnus sont supprimés silencieusement : la validation
 * stricte du corpus est la responsabilité de `@lexireveal/content-schema`.
 */
export function normalizeAnswer(raw: string): string {
  let out = "";
  for (const char of expandLigatures(raw)) {
    const letter = toPlainLetter(char);
    if (letter !== null) out += letter;
  }
  return out;
}

/** La saisie du joueur suit exactement la même normalisation que la réponse. */
export const normalizeGuess = normalizeAnswer;

/** Longueur normalisée : espaces, apostrophes et tirets ignorés (README §5). */
export function normalizedLength(raw: string): number {
  return normalizeAnswer(raw).length;
}

export class UnsupportedAnswerCharError extends Error {
  constructor(readonly char: string) {
    super(`Caractère non pris en charge dans une réponse : ${JSON.stringify(char)}`);
    this.name = "UnsupportedAnswerCharError";
  }
}

/**
 * Construit la grille d'affichage : positions saisissables et séparateurs.
 *
 * Lève une erreur sur un caractère inconnu plutôt que de l'ignorer : un défaut
 * de corpus doit être visible au chargement, pas au milieu d'une partie.
 */
export function buildDisplayCells(rawAnswer: string): DisplayCell[] {
  const cells: DisplayCell[] = [];
  let letterIndex = 0;

  for (const char of expandLigatures(rawAnswer)) {
    const separator = SEPARATORS.get(char);
    if (separator !== undefined) {
      cells.push({ kind: "separator", char: separator });
      continue;
    }

    const letter = toPlainLetter(char);
    if (letter === null) throw new UnsupportedAnswerCharError(char);

    cells.push({ kind: "letter", letterIndex, char: letter });
    letterIndex += 1;
  }

  return cells;
}

/** Une case de lettre dans la forme d'un mot : la lettre elle-même n'y figure pas. */
export const PATTERN_LETTER = "_";

/**
 * Forme d'un mot : `_` pour chaque lettre de la grille, séparateurs gardés.
 *
 * C'est tout ce qu'une partie classée reçoit du mot avant la fin (ADR-0006) :
 * la longueur et la place des tirets, espaces et apostrophes — exactement ce
 * que la grille montre déjà au joueur. « cœur » donne `_____` : la ligature
 * compte deux cases.
 */
export function buildAnswerPattern(rawAnswer: string): string {
  return buildDisplayCells(rawAnswer)
    .map((cell) => (cell.kind === "separator" ? cell.char : PATTERN_LETTER))
    .join("");
}

/**
 * La grille d'un mot dont on ne connaît que la forme.
 *
 * Les lettres portent `hiddenLetter` en attendant d'être révélées. Lève une
 * erreur sur un caractère qui n'est ni `_` ni un séparateur.
 */
export function buildCellsFromPattern(pattern: string, hiddenLetter: string): DisplayCell[] {
  const cells: DisplayCell[] = [];
  let letterIndex = 0;
  for (const char of pattern) {
    if (char === PATTERN_LETTER) {
      cells.push({ kind: "letter", letterIndex, char: hiddenLetter });
      letterIndex += 1;
      continue;
    }
    const separator = SEPARATORS.get(char);
    if (separator === undefined) throw new UnsupportedAnswerCharError(char);
    cells.push({ kind: "separator", char: separator });
  }
  return cells;
}
